// Per-strategy quote currency (ADR-0014). A strategy — backtest, paper, or live — is denominated in
// exactly ONE of these for its whole life: the symbol picker filters to pairs quoted in it, changing it
// prunes non-matching selections, the funds bar reads the balance OF it, and all amounts display in it.
// Mirrors the server's K2.QuoteCurrencies authority (packages/Utilities/QuoteCurrencies.cs) — the two
// lists must agree; the server validates authoritatively.

export const SUPPORTED_QUOTE_CURRENCIES = ['USD', 'USDC', 'BTC', 'ETH'];
export const DEFAULT_QUOTE_CURRENCY = 'USD';

// Display glyph per currency (the PositionsTable QUOTE_SYMBOLS precedent). USDC deliberately keeps its
// code suffix (not '$') in amount formatting so USD and USDC — strictly separate balances — are never
// visually conflated on the funds bar.
export const CURRENCY_GLYPHS = { USD: '$', BTC: '₿', ETH: 'Ξ' };

// Sensible display decimals: fiat/stable → 2; BTC/ETH amounts are small and need precision.
const DECIMALS = { USD: 2, USDC: 2, BTC: 6, ETH: 4 };

/** The quote currency of a pair in any spelling (BTC/USD, BTC-USD, BTC_USD) — upper-cased; '' when none. */
export function quoteOfPair(pair) {
  if (typeof pair !== 'string') return '';
  const match = pair.split(/[/\-_]/);
  return match.length > 1 ? match[match.length - 1].trim().toUpperCase() : '';
}

/** Whether a pair is quoted in `currency` (case-insensitive). */
export function matchesQuote(pair, currency) {
  return quoteOfPair(pair) === String(currency ?? '').trim().toUpperCase();
}

/** Keep only the selected pairs quoted in `currency` — the "changing the currency deselects
 *  non-matching pairs" rule. Returns a NEW array (Svelte runes={false} reassignment contract). */
export function pruneToQuote(selected, currency) {
  return (selected ?? []).filter((p) => matchesQuote(p, currency));
}

/** Filter the symbol-store rows (each carrying `.symbol` like 'BTC/USD') to one quote currency.
 *  A falsy currency filters nothing (the legacy unfiltered list). */
export function filterSymbolsByQuote(rows, currency) {
  if (!currency) return rows ?? [];
  return (rows ?? []).filter((s) => matchesQuote(s?.symbol, currency));
}

/** Format an amount in a quote currency: '$1,234.56', '₿0.052500', '1,234.56 USDC'. '—' on null. */
export function formatQuoteAmount(value, currency = DEFAULT_QUOTE_CURRENCY) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const cur = String(currency).toUpperCase();
  const decimals = DECIMALS[cur] ?? 2;
  const num = Number(value).toLocaleString('en-US', {
    minimumFractionDigits: Math.min(2, decimals),
    maximumFractionDigits: decimals,
  });
  const glyph = CURRENCY_GLYPHS[cur];
  return glyph ? `${glyph}${num}` : `${num} ${cur}`;
}

// Default per-org starting cash PER CURRENCY for fake-money venues (the old flat $1M generalized —
// 1M BTC would be absurd; these are round numbers of comparable economic magnitude).
export const DEFAULT_STARTING_CASH = { USD: 1_000_000, USDC: 1_000_000, BTC: 20, ETH: 500 };

/** The fake-venue default starting cash for a currency (falls back to the USD default). */
export function defaultStartingCash(currency) {
  return DEFAULT_STARTING_CASH[String(currency ?? '').toUpperCase()] ?? DEFAULT_STARTING_CASH.USD;
}
